import {Autocomplete, Checkbox, FormControlLabel, FormGroup, InputAdornment, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import Property from "crqlar-react-common/dist/components/Property";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {ACADEMY_LEVELS, academyTagList, academyTagText, withAcademyTag} from "../common/academyTags.util";
import {AcademyCourseTags, usedCourseValues, withCourseCredits, withCourseLevels} from "./academyCourse.util";

interface Props {
    service: Service;
    /** Every service, to offer trainers and rooms already in use. */
    services: Service[];
    onTagsChange: (tags: Record<string, unknown>) => void;
}

/**
 * A course's Akademie settings as labelled fields instead of raw tags: credits, levels, value,
 * trainer and room. Shown only where the Akademie exists, which today is Forsthofgut.
 */
const AcademyCourseSection = ({service, services, onTagsChange}: Props) => {
    const {t} = useTranslation()
    const tags = service.tags as Record<string, unknown> | undefined

    const levels = academyTagList(tags, AcademyCourseTags.LEVELS)
    const others = services.filter(s => s.id !== service.id)
    const trainers = usedCourseValues(others, AcademyCourseTags.TRAINER)
    const rooms = usedCourseValues(others, AcademyCourseTags.ROOM)

    const toggleLevel = (level: string, on: boolean) => onTagsChange(withCourseLevels(
        tags,
        ACADEMY_LEVELS.filter(l => l === level ? on : levels.includes(l)),
    ))

    // Free text with suggestions: a new trainer or room must be possible, a typo of an existing one
    // should not be the easy path.
    const suggestField = (id: string, key: string, options: string[]) => (
        <Autocomplete
            id={id}
            size={'small'}
            fullWidth
            freeSolo
            options={options}
            value={academyTagText(tags, key) ?? ''}
            onInputChange={(_, value, reason) => {
                if (reason !== 'reset') onTagsChange(withAcademyTag(tags, key, value))
            }}
            renderInput={(params) => <TextField {...params}/>}
        />
    )

    return (
        <AdditionalProperties
            title={t('Akademie course')}
            subtitle={t('What the Akademie shows employees and counts in the statistics.')}
            variant={'outlined'}
        >
            <Stack gap={{xs: 1.5, sm: 1}}>
                <Property
                    title={t('Credits')}
                    subtitle={t('Awarded once attendance is confirmed. 0 is valid, e.g. for part one of a two-part course.')}
                >
                    <TextField
                        id={'academy-credits'}
                        size={'small'}
                        fullWidth
                        inputMode={'numeric'}
                        value={academyTagText(tags, AcademyCourseTags.CREDITS) ?? ''}
                        onChange={(e) => onTagsChange(withCourseCredits(tags, e.target.value))}
                    />
                </Property>

                <Property
                    title={t('Levels')}
                    subtitle={t('Who may book it. None ticked: open to every team member.')}
                >
                    <FormGroup row>
                        {ACADEMY_LEVELS.map(level => (
                            <FormControlLabel
                                key={level}
                                label={t('Level {{level}}', {level})}
                                control={
                                    <Checkbox
                                        id={`academy-course-level-${level}`}
                                        checked={levels.includes(level)}
                                        onChange={(e) => toggleLevel(level, e.target.checked)}
                                    />
                                }
                            />
                        ))}
                    </FormGroup>
                </Property>

                <Property title={t('Course value')} subtitle={t('Shown to employees, as written.')}>
                    <TextField
                        id={'academy-course-value'}
                        size={'small'}
                        fullWidth
                        placeholder={'249,00'}
                        value={academyTagText(tags, AcademyCourseTags.COURSE_VALUE) ?? ''}
                        onChange={(e) => onTagsChange(withAcademyTag(tags, AcademyCourseTags.COURSE_VALUE, e.target.value))}
                        InputProps={{startAdornment: <InputAdornment position={'start'}>€</InputAdornment>}}
                    />
                </Property>

                <Property title={t('Trainer')}>
                    {suggestField('academy-trainer', AcademyCourseTags.TRAINER, trainers)}
                </Property>

                <Property title={t('Course room')}>
                    {suggestField('academy-room', AcademyCourseTags.ROOM, rooms)}
                </Property>
            </Stack>
        </AdditionalProperties>
    )
}

export default AcademyCourseSection
